// ABC в динамике (abcDynamics): карточка «Что изменилось», метка сдвига у блюда, блок пропавших блюд.
// Счётчики «Упали / Выросли / Новые» задают общий фильтр списка; «Пропали» раскрывает отдельный блок
// (у пропавших нет строки в текущем списке).
import React from 'react';
import { Box, ButtonBase, Collapse, Skeleton, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ACCORDION_BUTTON_SX, CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../../styles/tokens';
import { CHANGE_TITLES, changeFilter, sameFilter, toggleFilter } from '../../utils/abcList';
import { AbcLetter, money, plural } from './abcLook';

const ACCENT = SECTION_ACCENTS['/cost'];
const CHANGE_COLORS = { down: '#ef6b6b', up: '#66bb6a', new: '#64b5f6', gone: MUTED_TEXT_COLOR };
// Подпись сравнения (trendPeriod.comparisonFor) → «когда продавались» для пропавших блюд
const PAST_PERIOD = {
  'к прошлой неделе': 'на прошлой неделе',
  'к прошлому месяцу': 'в прошлом месяце',
  'к позапрошлой неделе': 'на позапрошлой неделе',
  'к позапрошлому месяцу': 'в позапрошлом месяце',
};
const focusSx = { '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' } };

export function AbcChangesCard({ counts, label, loading, filter, onFilter, goneOpen, onToggleGone }) {
  const items = [
    { key: 'down', title: CHANGE_TITLES.down },
    { key: 'up', title: CHANGE_TITLES.up },
    { key: 'new', title: CHANGE_TITLES.new },
    { key: 'gone', title: 'Пропали' },
  ];
  return (
    <Box component="section" aria-label="Что изменилось" sx={{ ...CARD_SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography component="h3" sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>Что изменилось</Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>{label}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
        {items.map((item) => {
          const count = counts?.[item.key] ?? 0;
          const active = item.key === 'gone' ? goneOpen : sameFilter(filter, changeFilter(item.key));
          const color = CHANGE_COLORS[item.key];
          const onClick = item.key === 'gone' ? onToggleGone : () => onFilter(toggleFilter(filter, changeFilter(item.key, label)));
          return (
            <ButtonBase
              key={item.key}
              disabled={loading || !count}
              onClick={onClick}
              aria-pressed={active}
              sx={{
                ...focusSx,
                minWidth: 0,
                minHeight: 44,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                backgroundColor: 'rgba(255,255,255,0.04)',
                boxShadow: active ? `inset 0 0 0 2px ${color}` : 'none',
                opacity: count || loading ? 1 : 0.5,
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color }}>{item.title}</Typography>
              {loading ? (
                <Skeleton variant="text" width={32} sx={{ fontSize: '24px' }} />
              ) : (
                <Typography sx={{ fontSize: '24px', fontWeight: 800, color: '#fff', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                  {count}
                </Typography>
              )}
            </ButtonBase>
          );
        })}
      </Box>
      <Typography sx={{ mt: 1, fontSize: '12px', color: MUTED_TEXT_COLOR }}>
        По группе прибыли: упали и выросли — через букву или при изменении прибыли на 20 % и больше
      </Typography>
    </Box>
  );
}

// Метка сдвига у блюда: «A → C» (упало) красным, «C → A» зелёным, «новое»; только заметные сдвиги
export function ChangeMark({ change, letter }) {
  if (!change || (change.direction !== 'new' && !change.notable)) return null;
  const color = CHANGE_COLORS[change.direction];
  const text = change.direction === 'new' ? 'новое' : `${change.prevLetter} → ${letter}`;
  const hint = change.direction === 'new' ? 'Не продавалось в прошлом периоде' : change.reason?.text || '';
  return (
    <Tooltip title={hint} disableHoverListener={!hint}>
      <Box
        component="span"
        sx={{ flexShrink: 0, px: 0.75, py: '1px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
          color, backgroundColor: `${color}26` }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

export function AbcGoneList({ gone, open, onToggle, label }) {
  if (!gone?.length) return null;
  return (
    <Box component="section" aria-label="Пропали из продаж" sx={{ ...CARD_SURFACE_SX, p: 1 }}>
      <ButtonBase onClick={onToggle} aria-expanded={open} sx={ACCORDION_BUTTON_SX}>
        <Box component="span" sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ display: 'block', fontSize: '15px', fontWeight: 700 }}>
            {`Пропали из продаж (${gone.length})`}
          </Typography>
          <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: MUTED_TEXT_COLOR }}>
            {`Продавались ${PAST_PERIOD[label] || 'в прошлом периоде'}, сейчас продаж нет — выведены из меню или в стопе`}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon aria-hidden="true" sx={{ flexShrink: 0, transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Box component="ul" sx={{ m: 0, px: 1, pt: 1, pb: 0.5, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {gone.map((dish) => (
            <Box component="li" key={dish.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <AbcLetter letter={dish.prevLetter} title={`Была группа ${dish.prevLetter} по прибыли`} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '14px', color: '#fff', wordBreak: 'break-word' }}>{dish.name}</Typography>
              <Typography sx={{ fontSize: '13px', color: CARD_LABEL_COLOR, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                {`${money(dish.profit)} прибыли`}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ px: 1, pb: 1, fontSize: '12px', color: MUTED_TEXT_COLOR }}>
          {`${gone.length} ${plural(gone.length, ['блюдо', 'блюда', 'блюд'])} · прибыль и группа — за прошлый период`}
        </Typography>
      </Collapse>
    </Box>
  );
}
